import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/', icon: 'ri-home-4-line', mobileName: 'Home' },
    { name: 'Over HerStart', href: '/over-herstart', icon: 'ri-information-line', mobileName: 'Over ons' },
    { name: 'Onze Aanpak', href: '/onze-aanpak', icon: 'ri-route-line', mobileName: 'Aanpak' },
    { name: 'Trainingen', href: '/trainingen', icon: 'ri-graduation-cap-line', mobileName: 'Trainingen' },
    { name: 'Samenwerkingen', href: '/samenwerkingen', icon: 'ri-links-line', mobileName: 'Partners' },
    { name: 'Doe mee', href: '/doe-mee', icon: 'ri-heart-line', mobileName: 'Doe mee' },
    { name: 'Contact', href: '/contact', icon: 'ri-mail-line', mobileName: 'Contact' },
  ];

  const isActive = (href: string) => location.pathname === href;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        className={`bg-white sticky top-0 z-50 transition-shadow duration-300 ${
          scrolled ? 'shadow-md' : 'shadow-sm'
        }`}
      >
        <nav className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img
                src="https://static.readdy.ai/image/1c53e264fa16b8cf25d5fb02610910c0/5f511fff67823575e4b070393f87a975.png"
                alt="HerStart logo"
                className="h-9 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-3 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                    isActive(item.href)
                      ? 'text-orange-500 border-b-2 border-orange-500'
                      : 'text-gray-700 hover:text-orange-500'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Hamburger button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden relative w-10 h-10 flex flex-col items-center justify-center gap-1.5 cursor-pointer rounded-lg hover:bg-orange-50 transition-colors duration-200"
              aria-label="Menu openen"
            >
              <span
                className={`block h-0.5 bg-gray-700 transition-all duration-300 origin-center ${
                  isMenuOpen ? 'w-5 rotate-45 translate-y-2' : 'w-5'
                }`}
              />
              <span
                className={`block h-0.5 bg-gray-700 transition-all duration-300 ${
                  isMenuOpen ? 'w-0 opacity-0' : 'w-4'
                }`}
              />
              <span
                className={`block h-0.5 bg-gray-700 transition-all duration-300 origin-center ${
                  isMenuOpen ? 'w-5 -rotate-45 -translate-y-2' : 'w-5'
                }`}
              />
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile overlay backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-40 lg:hidden transition-opacity duration-300 ${
          isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMenuOpen(false)}
      />

      {/* Mobile slide-in panel */}
      <div
        className={`fixed top-0 right-0 h-full w-[80vw] max-w-sm bg-white z-50 lg:hidden flex flex-col shadow-2xl transition-transform duration-300 ease-in-out ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Panel header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <img
            src="https://static.readdy.ai/image/1c53e264fa16b8cf25d5fb02610910c0/5f511fff67823575e4b070393f87a975.png"
            alt="HerStart logo"
            className="h-7 w-auto object-contain"
          />
          <button
            onClick={() => setIsMenuOpen(false)}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-orange-500 hover:bg-orange-50 transition-colors cursor-pointer"
            aria-label="Menu sluiten"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto px-4 py-4">
          <ul className="space-y-1">
            {navigation.map((item, index) => (
              <li key={item.name}>
                <Link
                  to={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center gap-4 px-4 py-3.5 rounded-xl text-base font-medium transition-all duration-200 cursor-pointer ${
                    isActive(item.href)
                      ? 'bg-orange-50 text-orange-500'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-orange-500'
                  }`}
                  style={{ transitionDelay: isMenuOpen ? `${index * 40}ms` : '0ms' }}
                >
                  <span className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${
                    isActive(item.href) ? 'bg-orange-100' : 'bg-gray-100'
                  }`}>
                    <i className={`${item.icon} text-lg ${isActive(item.href) ? 'text-orange-500' : 'text-gray-500'}`}></i>
                  </span>
                  <span>{item.mobileName}</span>
                  {isActive(item.href) && (
                    <i className="ri-arrow-right-s-line ml-auto text-orange-400 text-lg"></i>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom contact shortcuts */}
        <div className="px-4 py-5 border-t border-gray-100 space-y-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1">Direct contact</p>
          <a
            href="https://wa.me/31616708612"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-3 bg-green-50 text-green-700 rounded-xl font-medium hover:bg-green-100 transition-colors cursor-pointer"
          >
            <span className="w-9 h-9 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
              <i className="ri-whatsapp-line text-lg text-green-600"></i>
            </span>
            WhatsApp ons
          </a>
          <a
            href="tel:+31616708612"
            className="flex items-center gap-3 px-4 py-3 bg-gray-50 text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <span className="w-9 h-9 flex items-center justify-center bg-gray-200 rounded-lg flex-shrink-0">
              <i className="ri-phone-line text-lg text-gray-600"></i>
            </span>
            +31 6 16708612
          </a>
        </div>
      </div>
    </>
  );
};

export default Header;
